import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Small } from 'foundations-components/transitional/components/Small';
import I18n from 'I18n';
import { Button } from 'foundations-components/transitional/components/Button';
import { Form } from 'foundations-components/transitional/components/Form';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { PERSONAL_LINK_CREATE_ERROR, VCE_ACCOUNT_ID_ERROR } from 'MeetingsAdmin/constants/errors';
import EditOwnedBy from 'MeetingsAdmin/components/editLink/EditOwnedBy';
import { checkLinkSlug as checkLinkSlugRequest, createMeetingsLink, createMeetingsLinkForUser, fetchMeetingsLinkByIdInfo } from 'MeetingsAdmin/api/meetingsLinkAPI';
import MeetingsLink from 'MeetingsBase/models/MeetingsLink';
import { shouldUpdateVceLink } from 'MeetingsAdmin/utils/meetingsLinkValidation';
import { fetchMeetingsUserByUserId } from 'MeetingsAdmin/api/meetingsUserAPI';
import { useAvailableVideoConferenceExtensionByUserId } from 'MeetingsLib/hooks/videoConferenceExtensionHooks';
import EditClonedLinkSlug from './EditClonedLinkSlugV2';
import { useSelector } from 'react-redux';
import { getHasEditAllLinksScope, getUserId
// @ts-expect-error no type declarations yet
} from '../../selectors/AuthSelector';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function CloneMeetingsModal({
  link,
  handleCloseModal
}) {
  const linkId = link.id;
  const linkSlug = link.slug;
  const linkType = link.type;
  const linkUserId = link.userId;
  const creatingUserId = useSelector(state => getUserId(state));
  const canEditAllLinks = useSelector(getHasEditAllLinksScope);
  const [submit, setSubmit] = useState(false);
  const [name, setName] = useState(link.name ? `${link.name} (clone)` : 'clone');
  const [ownedBy, setOwnedBy] = useState(canEditAllLinks ? linkUserId : creatingUserId);
  const [slug, setSlug] = useState(linkSlug.split('/')[1] ? `${linkSlug.split('/')[1]}-clone` : 'clone');
  const [namespace, setNamespace] = useState('');
  const [showSlugCollision, setShowSlugCollision] = useState(false);
  const [ownedByValidationMessage, setOwnedByValidationMessage] = useState(null);
  const [cloneErrorMessage, setCloneErrorMessage] = useState(null);
  const [updateVceLink, setUpdateVceLink] = useState(false);
  const previouslySelectedUser = useRef(ownedBy);
  const validSlug = slug && !showSlugCollision;
  const handleNameChange = useCallback(event => {
    setName(event.target.value);
  }, []);
  const {
    videoConferenceApps,
    loading: loadingVideoConferenceApps
  } = useAvailableVideoConferenceExtensionByUserId({
    shouldSkip: previouslySelectedUser.current === ownedBy,
    userId: ownedBy
  });
  useEffect(() => {
    if (loadingVideoConferenceApps || !videoConferenceApps) {
      return;
    }
    previouslySelectedUser.current = ownedBy;
    setUpdateVceLink(shouldUpdateVceLink(link, videoConferenceApps));
  }, [videoConferenceApps, loadingVideoConferenceApps, ownedBy, link]);
  useEffect(() => {
    fetchMeetingsUserByUserId(ownedBy).then(data => {
      setNamespace(data.namespace);
    }).catch(emptyFunction);
  }, [ownedBy]);
  useEffect(() => {
    checkLinkSlugRequest(namespace, slug).then(isCollision => {
      setShowSlugCollision(isCollision);
    }).catch(emptyFunction);
  }, [slug, namespace]);
  const handleSlugChange = useCallback(event => {
    setSlug(event.target.value);
  }, []);
  const handleOwnedByChange = useCallback(userId => {
    setOwnedBy(userId);
    setOwnedByValidationMessage(null);
  }, []);
  const onWizardClose = useCallback(() => {
    const createLinkRequest = creatingUserId === ownedBy ? createMeetingsLink : createMeetingsLinkForUser;
    return fetchMeetingsLinkByIdInfo(linkId).then(linkBeingCloned => {
      const linkBeingClonedCustomParams = MeetingsLink.fromMeetingsLink(linkBeingCloned).get('customParams');
      const newCustomParams = updateVceLink ? linkBeingClonedCustomParams.set('location', '').set('videoConferenceExtensionAppId', null).set('videoConferenceExtensionAccountId', null) : linkBeingClonedCustomParams;
      const newLink = new MeetingsLink({
        customParams: newCustomParams,
        name,
        userId: ownedBy,
        slug: `${namespace}/${slug}`,
        type: linkType,
        meetingsUserId: ownedBy,
        isDefaultLink: false
      }).convertToJSON();
      // @ts-expect-error mismatched types
      delete newLink.id;
      return createLinkRequest(newLink);
    });
  }, [creatingUserId, ownedBy, linkId, linkType, name, namespace, slug, updateVceLink]);
  const exitModal = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);
  const handleSubmit = useCallback(() => {
    setSubmit(true);
    setCloneErrorMessage(null);
    onWizardClose().then(() => {
      handleCloseModal();
    }).catch(err => {
      setSubmit(false);
      if (err.responseJSON && err.responseJSON.message.includes(PERSONAL_LINK_CREATE_ERROR)) {
        setOwnedByValidationMessage( /*#__PURE__*/_jsx(FormattedMessage, {
          message: "cloneModal.error.invalidUser"
        }));
      }
      if (err.responseJSON && err.responseJSON.message.includes(VCE_ACCOUNT_ID_ERROR)) {
        setCloneErrorMessage( /*#__PURE__*/_jsx(FormattedMessage, {
          message: "cloneModal.error.vceError"
        }));
      } else {
        setCloneErrorMessage( /*#__PURE__*/_jsx(FormattedMessage, {
          message: "cloneModal.error.generalError"
        }));
      }
    });
  }, [setSubmit, onWizardClose, handleCloseModal]);
  const getTooltipError = useMemo(() => {
    if (!name) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cloneModal.validNameRequired"
      });
    } else if (!validSlug) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cloneModal.validSlugRequired"
      });
    } else {
      return null;
    }
  }, [name, validSlug]);
  return /*#__PURE__*/_jsx(Modal, {
    width: 540,
    onEsc: exitModal,
    "data-selenium-test": "clone-modal",
    children: /*#__PURE__*/_jsxs(Form, {
      onSubmit: evt => {
        evt.preventDefault();
        handleSubmit();
      },
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cloneModal.header"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: exitModal
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [/*#__PURE__*/_jsx(FormControl, {
          "data-test-id": "clone-internal-name",
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cloneModal.internalMeetingName"
          }),
          required: true,
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cloneModal.tooltip.name"
          }),
          error: !name,
          validationMessage: !name && /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cloneModal.validNameRequired"
          }),
          children: /*#__PURE__*/_jsx(TextInput, {
            value: name,
            onChange: handleNameChange,
            "data-selenium-test": "internal-name-input",
            "data-test-id": "clone-internal-name-input"
          })
        }), /*#__PURE__*/_jsx(EditOwnedBy, {
          editingLinkOwnerUserId: ownedBy,
          isNewLink: true,
          canEditAllLinks: canEditAllLinks,
          handleOwnedByChange: handleOwnedByChange,
          setSelectedUserOption: emptyFunction,
          validationMessage: ownedByValidationMessage,
          help: !canEditAllLinks ? I18n.text('editLink.tooltip.cloneForSelf') : ''
        }), /*#__PURE__*/_jsx(EditClonedLinkSlug, {
          namespace: namespace,
          slug: slug,
          handleSlugChange: handleSlugChange,
          showSlugCollision: showSlugCollision
        }), submit && /*#__PURE__*/_jsx(LoadingOverlay, {
          contextual: true,
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'cloneModal.loadingClonedMeeting'
          }),
          showLabel: true,
          layout: "centered"
        })]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Tooltip, {
          title: getTooltipError,
          children: /*#__PURE__*/_jsx(LoadingButton, {
            use: "primary",
            type: "submit",
            "data-test-id": "save-button",
            loading: loadingVideoConferenceApps,
            failed: cloneErrorMessage !== null,
            disabled: !validSlug || !name,
            "data-selenium-test": "clone-modal-save",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "cloneModal.saveButton"
            })
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          onClick: exitModal,
          "data-selenium-test": "clone-modal-close",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cloneModal.cancelButton"
          })
        }), cloneErrorMessage && /*#__PURE__*/_jsx(Small, {
          use: "error",
          weight: "medium",
          "data-test-id": "clone-error-message",
          marginBlock: "400",
          children: cloneErrorMessage
        })]
      })]
    })
  });
}